<template>
  <div class="pt-20 p-4 flex flex-col items-center bg-light-bg dark:bg-dark-bg min-h-screen text-light-text dark:text-dark-text">
    <AppSidebar />
    <div class="w-full max-w-6xl bg-white dark:bg-dark-box-bg rounded-lg shadow-md p-6 mt-6">
      <button
        @click="goToOrders"
        class="mb-4 text-teal-700 dark:text-teal-400 hover:underline flex items-center"
      >
        ← Wróć do Profilu Rozliczeń
      </button>

      <!-- Wyświetlanie komunikatu o ładowaniu -->
      <div v-if="loading" class="text-center">
        <p>Ładowanie danych zlecenia...</p>
      </div>
      <!-- Wyświetlanie komunikatu o błędzie -->
      <div v-else-if="errorMessage" class="text-center text-red-500">
        <p>{{ errorMessage }}</p>
      </div>
      <!-- Wyświetlanie szczegółów zlecenia -->
      <div v-else-if="order">
        <div class="relative mb-6">
          <h2 class="text-2xl font-bold">Szczegóły Rozliczenia</h2>
          <span class="absolute top-0 right-0 px-3 py-1 text-sm font-medium rounded-full bg-blue-500 text-white">
            {{ order.orderStatus || 'Brak statusu' }}
          </span>          
          <button
              @click="startEditing(order)"
              class="absolute top-8 right-0 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-400 flex items-center justify-center space-x-2"
          >
            <PencilIcon class="w-4 h-4" />
            <span>Edytuj</span>
          </button>
        </div>

        <div class="mb-6">
          <h3 class="text-lg font-semibold mb-2">
            Numer Zlecenia: {{ order.mpk || 'Brak danych' }}
          </h3>
          <p class="text-lg">
            <strong>Data:</strong> {{ formatDate(order.date) || 'Brak danych' }}
          </p>
          <p class="text-lg">
            <strong>Godzina Wyjazdowa:</strong> {{ formatTime(order.startDateTime) || 'Brak danych' }}
          </p>
          <p class="text-lg">
            <strong>Godzina Docelowa:</strong> {{ formatTime(order.endDateTime) || 'Brak danych' }}
          </p>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-4">Szczegóły Trasy</h4>
          <p><strong>Trasa wykonana:</strong> {{ order.passenger[0]?.pickupAddress }} → {{ order.passenger[0]?.dropoffAddress || 'Brak danych' }}</p>
          <p><strong>KM Łączne:</strong> {{ order.company?.onlyAB ? (order.distanceInKm ?? 'Brak danych') : (order.dispatcherKilometers ?? 'Brak danych') }} km</p>
          <p><strong>Autostrada:</strong> {{ order.highway ? 'Tak' : 'Nie' }}</p>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-4">Informacje o Pasażerze</h4>
          <p><strong>Pasażer:</strong> {{ order.passenger[0]?.firstName }} {{ order.passenger[0]?.lastName || 'Brak danych' }}</p>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-4">Rozliczenie</h4>
          <p><strong>Oczekiwanie:</strong> {{ formatWaitingTime(order.passenger[0]?.waitingStartTime, order.passenger[0]?.waitingEndTime) || 'Brak danych' }}</p>
          
          <p><strong>Cena za Km:</strong> {{ order.pricePerKilometer || 'Brak danych' }} PLN</p>
          <p><strong>Kilometry Dyspozytora:</strong> {{ order.dispatcherKilometers || 'Brak danych' }} KM</p>

          <p><strong>Bramki:</strong> {{ order.driverGates || 'Brak danych' }} PLN</p>
          <p><strong>Dodatki:</strong> {{ order.additvies || 'Brak danych' }} PLN</p>
          <p><strong>Ryczałt:</strong> {{ order.flatRate || 'Brak danych' }} PLN</p>
          <p class="text-xl font-bold mt-4"><strong>Suma Łączna:</strong> {{ order.totalAmount || 'Brak danych' }} PLN</p>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-4">Uwagi</h4>
          <div class="space-y-2">
            <p>
              <strong>Notatki Dyspozytora:</strong> 
              {{ order.notesDispatcher || 'Brak notatek dyspozytora' }}
            </p>
            <p>
              <strong>Notatki Kierowcy:</strong> 
              {{ order.notesDriver || 'Brak notatek kierowcy' }}
            </p>
            <p>
              <strong>Notatki Specyfikacji:</strong> 
              {{ order.notesSpec || 'Brak notatek specyfikacji' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <OrderCompanyEditModal
      :isOpen="isEditModalOpen"
      :orderToEdit="editingOrder"
      :errors="errors"
      :companies="companies"
      :passengers="passengers"
      :drivers="drivers"
      @close="closeEditModal"
      @submit="handleOrderEditSubmit"
  />
</template>

<script>
import orderService from '@/services/orderService';
import AppSidebar from '../AppSidebar.vue';
import { PencilIcon } from '@heroicons/vue/24/solid';
import { useAuthStore } from '@/stores/authStore';
import OrderCompanyEditModal from '../orders/OrderCompanyEditModal.vue';
import companiesService from '@/services/companiesService';
import passengersService from '@/services/passengersService';
import driversService from '@/services/driversService';

export default {
  name: 'OrderCompanyProfile',
  components: {
    AppSidebar,
    OrderCompanyEditModal,
    PencilIcon
  },
  setup() {
    const authStore = useAuthStore();
    if (authStore.isAuthenticated) {
      authStore.checkExpiration();
    }
    return { user: authStore.user };
  },
  data() {
    return {
      order: null,
      loading: true,
      errorMessage: null,
      editingOrder: null,
      showModal: false,
      isEditModalOpen: false,
      errors: {},
      companies: [],
      passengers: [],
      drivers: [],
    };
  },
  mounted() {
    this.fetchOrder();
    this.fetchCompanies();
    this.fetchDrivers();
    this.fetchPassengers();
  },
  methods: {
    async fetchOrder() {
      const orderId = this.$route.params.id;
      try {
        const response = await orderService.getOrderById(orderId);
        this.order = response.data;
        this.loading = false;
      } catch (error) {
        this.errorMessage = 'Błąd podczas pobierania danych zlecenia. Spróbuj ponownie.';
        this.loading = false;
      }
    },
    goToOrders() {
      const returnTo = this.$route.query.returnTo;
      const currentPage = this.$route.query.currentPage || '1';
      let id;

      if (returnTo === 'PayoffProfileAdmin' || returnTo === 'PayoffProfileDispatcher') {
        id = this.$route.query.payoffId;
      } else {
        id = this.$route.query.driverId || this.$route.query.passengerId || this.$route.query.companyId;
      }

      if (returnTo) {
        if (id) {
          this.$router.push({
            name: returnTo,
            params: { id: id },
            query: { currentPage: currentPage }
          });
        } else {
          this.$router.push({
            name: returnTo,
            query: { currentPage: currentPage }
          });
        }
      } else {
        const targetRoute = this.user.role === 'ROLE_ADMIN' ? 'OrdersPageAdmin' : 'OrdersPageDispatcher';
        this.$router.push({
          name: targetRoute,
          query: { currentPage: currentPage }
        });
      }
    },
    formatDate(date) {
      if (!date) return 'Brak danych';
      const d = new Date(date);
      return d.toLocaleDateString('pl-PL');
    },
    formatTime(dateTime) {
      if (!dateTime) return 'Brak danych';
      const d = new Date(dateTime);
      return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    },
    calculateWaitingTime(startTime, endTime) {
      const diffMs = new Date(endTime) - new Date(startTime);
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      // Zaczynamy liczyć dopiero po 60 minutach
      return diffMinutes > 60 ? diffMinutes - 60 : 0;
    },
    formatWaitingTime(start, end) {
      const waitingTime = this.calculateWaitingTime(start, end);
      if (waitingTime === null || isNaN(waitingTime)) return 'Brak danych';
      return `${waitingTime.toFixed(2)} minut`; // Apply toFixed only to valid numbers
    },
    startEditing(order) {
      this.showModal = false;
      this.openEditModal(order);
    },
    openEditModal(order) {
      this.editingOrder = order;
      this.isEditModalOpen = true;
    },
    closeEditModal() {
      this.isEditModalOpen = false;
      this.editingOrder = null;
    },
    handleOrderEditSubmit(editedOrder) {
        if (!this.loading) {
          this.submitEdit(editedOrder);
        }
    },
    async submitEdit(editedOrder) {
        if (this.loading) return;

        this.loading = true;
        try {
          const orderData = {
            mpk: editedOrder.mpk,
            companyId: editedOrder.company.id,
            passenger: editedOrder.passenger,
            driverId: editedOrder.driverId,
            // dispatcherId: this.user.uid,
            notesDispatcher: editedOrder.notesDispatcher,
            notesDriver: editedOrder.notesDriver,
            notesSpec: editedOrder.notesSpec,
            distanceInKm: editedOrder.distanceInKm,
            dispatcherKilometers: editedOrder.dispatcherKilometers,
            highway: editedOrder.highway,
            pricePerKilometer: editedOrder.pricePerKilometer,
            driverGates: editedOrder.driverGates,
            additvies: editedOrder.additvies,
            flatRate: editedOrder.flatRate,
            totalAmount: editedOrder.totalAmount,
            orderStatus: editedOrder.orderStatus
          };

          await orderService.updateOrder(editedOrder.id, orderData);
          // this.showMessage("Zlecenie zostało pomyślnie zaktualizowane.", "success");

          await this.fetchOrder();
          this.closeEditModal();
        } catch (error) {
          // this.showMessage("Wystąpił błąd podczas aktualizacji zlecenia.", "error");

          console.error('Błąd podczas aktualizacji zamówienia:', error);
        } finally {
          this.loading = false;
        }
    },
    async fetchCompanies() {
        try {
          const response = await companiesService.getAllCompanies();
          this.companies = response.data;
        } catch (error) {
          console.error('Błąd podczas pobierania firm:', error);
        }
      },
    async fetchPassengers() {
        try {
          const response = await passengersService.getAllPassengers();
          this.passengers = response.data;
        } catch (error) {
          console.error('Błąd podczas pobierania pasażerów:', error);
        }
      },
    async fetchDrivers() {
        try {
          const response = await driversService.getAllDrivers();
          this.drivers = response.data;
        } catch (error) {
          console.error('Błąd podczas pobierania kierowców:', error);
        }
      },
  },
};
</script>

<style scoped>
/* Style pozostają bez zmian */
</style>